import type { ICellRendererParams } from "ag-grid-community"
import { subYears } from "date-fns"
import { flatten, isEqual, max, min, sum, sumBy } from "lodash"
import type { ReactNode } from "react"

import { GMROIBreakdownHoverCard } from "~/components/reusable/GMROIBreakdownHoverCard"
import { InventoryTurnBreakdownHoverCard } from "~/components/reusable/InventoryTurnBreakdownHoverCard"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { createNumberColumn, createSumColumn } from "~/utils/columnTemplates"
import { DateFormatType, formatDate, startOfTodayUTC } from "~/utils/datetime"
import { formatNumber } from "~/utils/formatting"
import { roundNumber } from "~/utils/helpers"
import { isLifecycleInactive } from "~/utils/lifecycleUtils"
import {
  calcReturnRate,
  extractVariantSummaries,
  ProductTableColumn,
  returnRateFromTotals,
} from "~/utils/productTableUtils"
import { isAllFashion } from "~/utils/productTypeUtils"

import { approximateProfit } from "../utils/profitUtils"
import { leafVariantIds, numberOrDash, tooltipOrField } from "./cellHelpers"
import type { ColumnSpec } from "./types"

export interface SalesColumnCtx {
  currency: string | undefined
}

type SalesSpec = ColumnSpec<IVariantSummary, SalesColumnCtx>

/** Sales excluding the pre-orders booked against the same period a year earlier. */
export function salesWithoutPreOrders(row: IVariantSummary): number {
  const sales = row.salesQuantity ?? 0
  const preOrders = row.lastYearPreOrders ?? 0
  return sales - preOrders
}

export function profit(row: IVariantSummary): number {
  return approximateProfit({
    revenue: row.revenue,
    costPrice: row.costPrice,
    salesQuantity: row.salesQuantity,
    returns: row.lastYearReturns,
  })
}

export const SUGGESTED_ORDER_COLUMN: SalesSpec = {
  colId: ProductTableColumn.SuggestedOrder,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.SuggestedOrder,
      field: "suggestedOrder",
      cellClass: (params) => {
        const entries: IVariantSummary[] = extractVariantSummaries(params)
        if (isAllFashion(entries) || isLifecycleInactive(entries)) {
          return "text-fg-muted"
        }
      },
      valueFormatter: (params) => {
        const entries: IVariantSummary[] = extractVariantSummaries(params)
        if (isAllFashion(entries) || isLifecycleInactive(entries)) {
          return "N/A"
        }

        return params.value !== undefined ? formatNumber(params.value) : "N/A"
      },
      tooltip: (params) => {
        const entries: IVariantSummary[] = extractVariantSummaries(params)

        const suggestedOrders = flatten(
          entries.map((entry) =>
            Object.entries(entry.stockOutInfoWarehouse ?? {}).map(
              ([warehouseId, stockOutInfo]) => ({
                warehouseId,
                quantity: sumBy(stockOutInfo, "suggestedOrder"),
              }),
            ),
          ),
        )

        const skuIds = entries.flatMap((entry) => entry.skuIds)
        const lifecycleStatus = entries?.[0]?.settings?.lifecycleStatus ?? undefined
        const isFashionAll = isAllFashion(entries)

        return { suggestedOrders, skuIds, lifecycleStatus, isFashion: isFashionAll }
      },
      tooltipComponentParams: { tooltipType: "SuggestedOrder" },
    }),
}

export const SALES_LAST_30_DAYS_COLUMN: SalesSpec = {
  colId: ProductTableColumn.SalesLast30Days,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.SalesLast30Days,
      field: "last30DaysSales",
      initialWidth: 130,
      valueFormatter: numberOrDash,
      tooltip: tooltipOrField(leafVariantIds, "salesQuantity"),
      tooltipComponentParams: { tooltipType: "SalesAndForecast" },
    }),
}

export const SALES_WITHOUT_PRE_ORDERS_COLUMN: SalesSpec = {
  colId: ProductTableColumn.SalesWithoutPreOrders,
  calculate: salesWithoutPreOrders,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.SalesWithoutPreOrders,
      minWidth: 180,
      tooltip: leafVariantIds,
      tooltipComponentParams: { tooltipType: "SalesAndForecast", type: "sales" },
    }),
}

export const EXPECTED_RETURNS_COLUMN: SalesSpec = {
  colId: ProductTableColumn.ExpectedReturns,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.ExpectedReturns,
      field: "forecastedReturnsQuantity",
      initialWidth: 130,
      valueFormatter: numberOrDash,
    }),
}

export const PRE_ORDERS_LAST_YEAR_COLUMN: SalesSpec = {
  colId: ProductTableColumn.PreOrdersLastYear,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.PreOrdersLastYear,
      field: "lastYearPreOrders",
      initialWidth: 130,
      tooltipComponentParams: {
        showDateRange: "lastYear",
      },
    }),
}

export const PROFIT_COLUMN: SalesSpec = {
  colId: ProductTableColumn.Profit,
  calculate: profit,
  colDef: (ctx) =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.Profit,
      initialWidth: 130,
      useFormatWithSuffix: true,
      suffix: ctx.currency ?? "DKK",
      tooltipComponentParams: {
        showDateRange: "lastYear",
      },
    }),
}

export const SALES_COLUMN: SalesSpec = {
  colId: ProductTableColumn.Sales,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.Sales,
      field: "salesQuantity",
      initialWidth: 130,
      tooltip: tooltipOrField(leafVariantIds, "salesQuantity"),
      tooltipComponentParams: { tooltipType: "SalesAndForecast", type: "sales" },
    }),
}

export const NET_SALES_COLUMN: SalesSpec = {
  colId: ProductTableColumn.NetSales,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.NetSales,
      valueGetter: (p) => (p.data?.salesQuantity ?? 0) - (p.data?.lastYearReturns ?? 0),
      initialWidth: 130,
      tooltip: leafVariantIds,
      tooltipComponentParams: {
        tooltipType: "SalesAndForecast",
        type: "sales",
        netSalesMode: "only",
      },
    }),
}

export const RETURNS_COLUMN: SalesSpec = {
  colId: ProductTableColumn.Returns,
  colDef: () =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.Returns,
      field: "lastYearReturns",
      initialWidth: 130,
      valueFormatter: numberOrDash,
      tooltip: tooltipOrField(leafVariantIds, "lastYearReturns"),
      tooltipComponentParams: { tooltipType: "Returns", showDateRange: "lastYear" },
    }),
}

export const RETURN_RATE_COLUMN: SalesSpec = {
  colId: ProductTableColumn.ReturnRate,
  colDef: () =>
    createNumberColumn<IVariantSummary>({
      colId: ProductTableColumn.ReturnRate,
      initialWidth: 130,
      valueGetter: (p) =>
        returnRateFromTotals(p.data?.lastYearReturns ?? 0, p.data?.salesQuantity ?? 0),
      aggFunc: (params) => {
        const entries: IVariantSummary[] = extractVariantSummaries(params)
        return calcReturnRate(entries)
      },
      equals: (v1, v2) => isEqual(v1, v2),
      percentage: true,
      tooltipComponentParams: {
        showDateRange: "lastYear",
      },
    }),
}

export const REVENUE_COLUMN: SalesSpec = {
  colId: ProductTableColumn.Revenue,
  colDef: (ctx) =>
    createSumColumn<IVariantSummary>({
      colId: ProductTableColumn.Revenue,
      initialWidth: 130,
      field: "revenue",
      useFormatWithSuffix: true,
      suffix: ctx.currency ?? "DKK",
      tooltipComponentParams: {
        showDateRange: "lastYear",
      },
    }),
}

/** Two years of sales history is the minimum for a growth figure. */
function hasEnoughSalesData(minSoldSince: Date | null | undefined): boolean {
  return !!minSoldSince && minSoldSince < subYears(startOfTodayUTC(), 2)
}

/** Growth in cost-weighted sales, last year against the year before, over one or more rows. */
export function salesGrowth(rows: IVariantSummary[]): number | undefined {
  const totalLastYear = sumBy(rows, (row) => row.salesQuantity * (row.costPrice ?? 1))
  const totalSecondLastYear =
    sumBy(rows, (row) => row.lastTwoYearSales * (row.costPrice ?? 1)) - totalLastYear

  const growth = (totalLastYear - totalSecondLastYear) / totalSecondLastYear
  const minSoldSince = min(rows.map((row) => row.soldSince))

  if (Number.isFinite(growth) && hasEnoughSalesData(minSoldSince)) {
    return growth
  }
  return undefined
}

export const SALES_GROWTH_COLUMN: SalesSpec = {
  colId: ProductTableColumn.SalesGrowth,
  calculate: (row) => salesGrowth([row]),
  aggregate: salesGrowth,
  colDef: () =>
    createNumberColumn<IVariantSummary>({
      colId: ProductTableColumn.SalesGrowth,
      percentage: true,
      tooltip: (params) => {
        const entries = extractVariantSummaries(params)

        const totalLastYearUnits = sumBy(entries, (entry) => entry.salesQuantity)
        const totalSecondLastYearUnits =
          sumBy(entries, (entry) => entry.lastTwoYearSales) - totalLastYearUnits

        const lastYearDate = formatDate(subYears(startOfTodayUTC(), 1), DateFormatType.MonthYear)
        const nowDate = formatDate(startOfTodayUTC(), DateFormatType.MonthYear)
        const minSoldSince = min(entries.map((v) => v.soldSince))

        const enoughSalesData = hasEnoughSalesData(minSoldSince)
        const growth = (totalLastYearUnits - totalSecondLastYearUnits) / totalSecondLastYearUnits
        const soldSince = minSoldSince ? formatDate(minSoldSince, DateFormatType.MonthYear) : "-"
        return enoughSalesData && Number.isFinite(growth)
          ? `Sales ${totalSecondLastYearUnits - totalLastYearUnits < 0 ? "increased" : "decreased"} from ${formatNumber(totalSecondLastYearUnits)} to ${formatNumber(totalLastYearUnits)} units in the period from ${lastYearDate} to ${nowDate}.`
          : `Not enough sales data to calculate growth. Sold since ${soldSince}.`
      },
    }),
}

export const SELL_THROUGH_RATE_COLUMN: SalesSpec = {
  colId: ProductTableColumn.SellThroughRate,
  aggregate: (entries) => {
    const totalSales = max([sum(entries.map((variant) => variant?.totalSales ?? 0)), 0])!
    const totalStockLevel = max([sum(entries.map((entry) => entry.stockLevel)), 0])!

    return totalSales / (totalSales + totalStockLevel)
  },
  colDef: () =>
    createNumberColumn<IVariantSummary>({
      colId: ProductTableColumn.SellThroughRate,
      valueGetter: (params) => {
        const totalSales = max([params.data?.totalSales ?? 0, 0])!
        const stockLevel = max([params.data?.stockLevel ?? 0, 0])!
        return totalSales / (totalSales + stockLevel)
      },
      percentage: true,
      tooltip: (params) => {
        const entries: IVariantSummary[] = extractVariantSummaries(params)
        const totalSales = max([sum(entries.map((entry) => entry.totalSales)) ?? 0, 0])!
        const totalStockLevel = max([sum(entries.map((entry) => entry.stockLevel)) ?? 0, 0])!
        return `${formatNumber(totalSales)} / (${formatNumber(totalSales)} + ${formatNumber(totalStockLevel)})) * 100`
      },
      tooltipComponentParams: { tooltipType: "SellThroughRate" },
    }),
}

// One pass: these run per cell per read, on every paint, sort comparison and filter pass.
function inventoryTotals(rows: IVariantSummary[]) {
  let revenue = 0
  let cogs = 0
  let avgInv = 0
  for (const row of rows) {
    revenue += row.revenue ?? 0
    cogs += row.inventoryTurnCogs ?? 0
    avgInv += row.inventoryTurnAvgInvValue ?? 0
  }
  return { revenue, cogs, avgInv }
}

type InventoryTotals = ReturnType<typeof inventoryTotals>

/** Cost of goods sold over average inventory value. */
const turnOf = ({ cogs, avgInv }: InventoryTotals): number | null =>
  avgInv > 0 ? cogs / avgInv : null

/** Gross margin over average inventory value. */
const gmroiOf = ({ revenue, cogs, avgInv }: InventoryTotals): number | null =>
  avgInv > 0 ? (revenue - cogs) / avgInv : null

function earliestInventoryTurnStart(entries: IVariantSummary[]): Date | null {
  const dates = entries.flatMap((e) =>
    e.inventoryTurnStartDate ? [new Date(e.inventoryTurnStartDate).getTime()] : [],
  )
  const minMs = dates.length > 0 ? dates.reduce((a, b) => (a < b ? a : b)) : null
  return minMs != null ? new Date(minMs) : null
}

type InventoryBreakdown = InventoryTotals & {
  costPrice: number | null
  startDate: Date | string | null
}

function inventoryBreakdown(params: ICellRendererParams<IVariantSummary>): InventoryBreakdown {
  if (params.node?.group) {
    const entries: IVariantSummary[] = extractVariantSummaries(params)
    return {
      ...inventoryTotals(entries),
      // Any leaf with a positive cost price means cost price isn't the blocker.
      costPrice: entries.reduce<number>((acc, e) => Math.max(acc, e.costPrice ?? 0), 0),
      startDate: earliestInventoryTurnStart(entries),
    }
  }
  return {
    revenue: params.data?.revenue ?? 0,
    cogs: params.data?.inventoryTurnCogs ?? 0,
    avgInv: params.data?.inventoryTurnAvgInvValue ?? 0,
    costPrice: params.data?.costPrice ?? null,
    startDate: params.data?.inventoryTurnStartDate ?? null,
  }
}

const formatRatio = (ratio: number | null) => (ratio != null ? String(roundNumber(ratio, 2)) : "-")

/** Group rows read `params.value`, to stay consistent with sort and filter. */
function cellRatio(
  params: ICellRendererParams<IVariantSummary>,
  breakdown: InventoryBreakdown,
  ratio: (totals: InventoryTotals) => number | null,
): number | null {
  if (params.node?.group) return params.value
  return ratio(breakdown)
}

/**
 * A ratio over average inventory value, shown against the breakdown it was computed from. A
 * group's ratio is the ratio of its summed parts, never the sum of per-leaf ratios, so the value
 * getter aggregates from the leaves itself and `aggregate` mirrors it for aggData.
 */
function ratioColumn(
  colId: ProductTableColumn,
  ratio: (totals: InventoryTotals) => number | null,
  card: (
    breakdown: InventoryBreakdown,
    currency: string | undefined,
    children: ReactNode,
  ) => ReactNode,
): SalesSpec {
  const fromRows = (rows: IVariantSummary[]) => ratio(inventoryTotals(rows))
  return {
    colId,
    aggregate: fromRows,
    render: (params, ctx) => {
      if (params.node?.rowPinned) return <span>{formatRatio(params.value)}</span>
      const breakdown = inventoryBreakdown(params)
      const value = formatRatio(cellRatio(params, breakdown, ratio))
      return card(breakdown, ctx.currency, <div className="w-full">{value}</div>)
    },
    colDef: () =>
      createNumberColumn<IVariantSummary>({
        colId,
        decimals: 2,
        valueGetter: (params) => fromRows(extractVariantSummaries(params)),
        cellStyle: { display: "flex", alignItems: "center", overflow: "hidden" },
      }),
  }
}

export const INVENTORY_TURN_COLUMN = ratioColumn(
  ProductTableColumn.InventoryTurn,
  turnOf,
  (breakdown, currency, children) => (
    <InventoryTurnBreakdownHoverCard
      cogs={breakdown.cogs}
      avgInvValue={breakdown.avgInv}
      costPrice={breakdown.costPrice}
      currency={currency}
      startDate={breakdown.startDate}
    >
      {children}
    </InventoryTurnBreakdownHoverCard>
  ),
)

export const GMROI_COLUMN = ratioColumn(
  ProductTableColumn.GMROI,
  gmroiOf,
  (breakdown, currency, children) => (
    <GMROIBreakdownHoverCard
      revenue={breakdown.revenue}
      cogs={breakdown.cogs}
      avgInvValue={breakdown.avgInv}
      costPrice={breakdown.costPrice}
      currency={currency}
      startDate={breakdown.startDate}
    >
      {children}
    </GMROIBreakdownHoverCard>
  ),
)

export const SALES_COLUMN_SPECS: readonly SalesSpec[] = [
  SUGGESTED_ORDER_COLUMN,
  SALES_LAST_30_DAYS_COLUMN,
  SALES_COLUMN,
  NET_SALES_COLUMN,
  SALES_WITHOUT_PRE_ORDERS_COLUMN,
  RETURNS_COLUMN,
  EXPECTED_RETURNS_COLUMN,
  RETURN_RATE_COLUMN,
  PRE_ORDERS_LAST_YEAR_COLUMN,
  REVENUE_COLUMN,
  PROFIT_COLUMN,
  SALES_GROWTH_COLUMN,
  SELL_THROUGH_RATE_COLUMN,
  INVENTORY_TURN_COLUMN,
  GMROI_COLUMN,
]
